import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import PortalLayout from "../portal/PortalLayout";
import { StatusPill, SERVICE_LABELS, formatDate } from "../portal/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { CheckCircle2, XCircle, Calendar, Briefcase, Car, AlertTriangle, ShieldAlert } from "lucide-react";

const DAYS = [
  { k: "mon", l: "Mon" },
  { k: "tue", l: "Tue" },
  { k: "wed", l: "Wed" },
  { k: "thu", l: "Thu" },
  { k: "fri", l: "Fri" },
  { k: "sat", l: "Sat" },
  { k: "sun", l: "Sun" },
];

const SHIFTS = [
  { k: "morning", l: "Morning" },
  { k: "afternoon", l: "Afternoon" },
  { k: "evening", l: "Evening" },
  { k: "overnight", l: "Overnight" },
];

export function CaregiverHome() {
  const { user, refresh } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const profile = user?.caregiver_profile || {};

  const load = async () => {
    try {
      const { data } = await api.get("/assignments");
      setAssignments(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const toggleAvailability = async (v) => {
    try {
      await api.patch("/caregiver/profile", { is_available: v });
      await refresh();
      toast.success(v ? "You're now marked Available." : "You're marked Unavailable.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const respond = async (id, response) => {
    try {
      await api.patch(`/assignments/${id}/respond`, { response });
      toast.success(`Assignment ${response}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const offered = assignments.filter((a) => a.status === "offered");
  const upcoming = assignments.filter((a) => a.status === "accepted");
  const past = assignments.filter((a) => a.status === "declined");

  return (
    <PortalLayout title="My Shifts">
      {!user?.approved && (
        <div data-testid="caregiver-pending-approval" className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Awaiting approval</p>
            <p className="text-sm text-amber-800 mt-0.5">Our team is reviewing your profile. Once approved, you'll receive shift assignments here.</p>
          </div>
        </div>
      )}

      {/* Availability card */}
      <div
        data-testid="availability-card"
        className={`mb-6 p-5 sm:p-6 rounded-3xl border-2 transition-colors ${
          profile.is_available ? "bg-emerald-50 border-emerald-200" : "bg-white border-[#E5DDC9]"
        }`}
      >
        <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#64748B]">Status</p>
            <p className={`font-serif text-3xl ${profile.is_available ? "text-emerald-700" : "text-[#005165]"}`}>
              {profile.is_available ? "Available for shifts" : "Unavailable"}
            </p>
            <p className="text-sm text-[#64748B] mt-1">Toggle anytime — admin sees your status instantly.</p>
          </div>
          <Switch
            data-testid="availability-toggle"
            checked={!!profile.is_available}
            onCheckedChange={toggleAvailability}
            disabled={!user?.approved}
            className="data-[state=checked]:bg-emerald-600 scale-125"
          />
        </div>
      </div>

      {/* Offered assignments */}
      {offered.length > 0 && (
        <section className="mb-8">
          <h2 className="font-serif text-2xl text-[#005165] mb-3 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#AC9667]" /> Offers awaiting your response
          </h2>
          <div className="grid gap-4">
            {offered.map((a) => (
              <article
                key={a.id}
                data-testid={`offered-${a.id}`}
                className="bg-white rounded-2xl border border-[#E5DDC9] p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#64748B]">Client</p>
                    <p className="font-serif text-xl text-[#005165]">{a.client_name}</p>
                    {a.care_recipient_name && (
                      <p className="text-sm text-[#4B5563]">Caring for: {a.care_recipient_name}</p>
                    )}
                    <p className="text-xs text-[#64748B] mt-1">Offered {formatDate(a.created_at)}</p>
                  </div>
                  <StatusPill status={a.status} urgent={a.urgent} />
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {a.services?.map((s) => (
                    <span key={s} className="px-3 py-1 rounded-full text-xs bg-[#F8F9FA] text-[#005165] border border-[#E5DDC9]">
                      {SERVICE_LABELS[s] || s}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`accept-${a.id}`}
                    onClick={() => respond(a.id, "accepted")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-5"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept
                  </Button>
                  <Button
                    data-testid={`decline-${a.id}`}
                    onClick={() => respond(a.id, "declined")}
                    variant="outline"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50 rounded-full h-10 px-5"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Decline
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Active shifts */}
      <section className="mb-8">
        <h2 className="font-serif text-2xl text-[#005165] mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#AC9667]" /> Active assignments
        </h2>
        {loading ? (
          <p className="text-sm text-[#64748B] py-8">Loading...</p>
        ) : upcoming.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E5DDC9] p-8 text-center text-sm text-[#64748B]">
            No active shifts. New offers will appear at the top.
          </div>
        ) : (
          <div className="grid gap-3">
            {upcoming.map((a) => (
              <article key={a.id} data-testid={`active-${a.id}`} className="bg-white rounded-2xl border border-[#E5DDC9] p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                  <div>
                    <p className="font-serif text-xl text-[#005165]">{a.client_name}</p>
                    {a.care_recipient_name && <p className="text-sm text-[#4B5563]">Caring for: {a.care_recipient_name}</p>}
                  </div>
                  <StatusPill status="in_progress" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {a.services?.map((s) => (
                    <span key={s} className="px-2.5 py-0.5 rounded-full text-xs bg-[#F8F9FA] text-[#005165] border border-[#E5DDC9]">
                      {SERVICE_LABELS[s] || s}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h2 className="font-serif text-xl text-[#64748B] mb-3">History</h2>
          <div className="space-y-2">
            {past.map((a) => (
              <div key={a.id} className="flex items-center justify-between bg-white border border-[#E5DDC9] rounded-xl p-4 text-sm">
                <span className="text-[#1F2937]">{a.client_name} · {formatDate(a.created_at)}</span>
                <StatusPill status={a.status} />
              </div>
            ))}
          </div>
        </section>
      )}
    </PortalLayout>
  );
}

export function CaregiverProfile() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const initial = user?.caregiver_profile || {};
  const [form, setForm] = useState({
    phone: user?.phone || "",
    years_experience: initial.years_experience || 0,
    certifications: initial.certifications || "",
    transportation: !!initial.transportation,
    availability_days: initial.availability_days || [],
    availability_shifts: initial.availability_shifts || [],
    bio: initial.bio || "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      availability_days: f.availability_days.includes(d)
        ? f.availability_days.filter((x) => x !== d)
        : [...f.availability_days, d],
    }));
  const toggleShift = (s) =>
    setForm((f) => ({
      ...f,
      availability_shifts: f.availability_shifts.includes(s)
        ? f.availability_shifts.filter((x) => x !== s)
        : [...f.availability_shifts, s],
    }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch("/caregiver/profile", {
        ...form,
        years_experience: parseInt(form.years_experience) || 0,
      });
      await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalLayout title="My Availability & Profile">
      <form onSubmit={save} data-testid="caregiver-profile-form" className="bg-white rounded-3xl border border-[#E5DDC9] p-6 sm:p-8 max-w-3xl space-y-7">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#64748B] mb-3">Required</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cp-phone">Phone *</Label>
              <Input id="cp-phone" data-testid="cp-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
            <div>
              <Label htmlFor="cp-exp">Years of Experience *</Label>
              <Input id="cp-exp" data-testid="cp-exp" type="number" min="0" value={form.years_experience} onChange={(e) => update("years_experience", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Available days</Label>
          <p className="text-xs text-[#64748B] mb-3">Tap to toggle each day you can work.</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const on = form.availability_days.includes(d.k);
              return (
                <button
                  type="button"
                  key={d.k}
                  data-testid={`day-${d.k}`}
                  onClick={() => toggleDay(d.k)}
                  className={`w-14 h-14 rounded-2xl border text-sm font-medium transition-colors ${
                    on ? "bg-[#005165] text-[#FFFFFF] border-[#005165]" : "bg-[#FFFFFF] text-[#005165] border-[#E5DDC9] hover:border-[#AC9667]"
                  }`}
                >
                  {d.l}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Available shifts</Label>
          <div className="flex flex-wrap gap-2">
            {SHIFTS.map((s) => {
              const on = form.availability_shifts.includes(s.k);
              return (
                <button
                  type="button"
                  key={s.k}
                  data-testid={`shift-${s.k}`}
                  onClick={() => toggleShift(s.k)}
                  className={`px-5 h-11 rounded-full border text-sm transition-colors ${
                    on ? "bg-[#AC9667] text-white border-[#AC9667]" : "bg-[#FFFFFF] text-[#005165] border-[#E5DDC9] hover:border-[#AC9667]"
                  }`}
                >
                  {s.l}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-[#FFFFFF] border border-[#E5DDC9]">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-[#AC9667]" />
            <div>
              <p className="font-medium text-[#005165]">Reliable transportation</p>
              <p className="text-xs text-[#64748B]">Required for transportation assignments.</p>
            </div>
          </div>
          <Switch
            data-testid="transportation-switch"
            checked={form.transportation}
            onCheckedChange={(v) => update("transportation", v)}
            className="data-[state=checked]:bg-emerald-600"
          />
        </div>

        <div>
          <Label htmlFor="cp-cert">Certifications</Label>
          <Input id="cp-cert" data-testid="cp-cert" placeholder="CNA, CPR, HHA..." value={form.certifications} onChange={(e) => update("certifications", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
        </div>

        <div>
          <Label htmlFor="cp-bio">Short bio (optional)</Label>
          <Textarea id="cp-bio" data-testid="cp-bio" rows={4} value={form.bio} onChange={(e) => update("bio", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
        </div>

        <Button type="submit" data-testid="cp-save" disabled={loading} className="bg-[#005165] text-[#FFFFFF] rounded-full h-12 px-8 btn-shimmer">
          {loading ? "Saving..." : "Save Profile"}
        </Button>
      </form>
    </PortalLayout>
  );
}
